import type { CSSProperties, ReactNode } from 'react';
import type { AdFormatId } from '@/mocks/advertise';

/** Brand colors for the drawings. */
const AD = '#2f51d6';
const AD_SOFT = '#e8edff';
const LINE = '#e6e8ee';

const Bar = ({ w = '100%', h = 6, c = LINE }: { w?: string; h?: number; c?: string }) => (
  <span className="block rounded-full" style={{ width: w, height: h, background: c }} />
);

function AdBlock({ label = 'AD', className = '' }: { label?: string; className?: string }) {
  return (
    <div className={`rounded-md flex items-center justify-center text-white font-extrabold tracking-wide ${className}`} style={{ background: AD }}>
      {label}
    </div>
  );
}

/** Desktop content for each format (inside the browser frame). */
function Desktop({ id }: { id: AdFormatId }) {
  switch (id) {
    case 'banner':
      return (
        <div className="space-y-2">
          <AdBlock className="h-8 text-sm" label="AD · 728×90" />
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2 space-y-1.5"><Bar w="80%" /><Bar /><Bar w="60%" /><Bar w="90%" /></div>
            <div className="rounded bg-[#f2f3f5] h-12" />
          </div>
        </div>
      );
    case 'native':
      return (
        <div className="space-y-2">
          {[0, 1].map((i) => (
            <div key={i} className="flex gap-2 items-center"><div className="w-10 h-8 rounded bg-[#f2f3f5]" /><div className="flex-1 space-y-1"><Bar w="70%" /><Bar w="45%" /></div></div>
          ))}
          <div className="flex gap-2 items-center rounded-md p-1.5" style={{ background: AD_SOFT }}>
            <AdBlock className="w-10 h-8 text-[9px]" label="AD" />
            <div className="flex-1 space-y-1"><Bar w="75%" c={AD} /><Bar w="40%" c="#b8c6ff" /></div>
            <span className="text-[8px] font-bold text-white rounded px-1.5 py-0.5" style={{ background: AD }}>Open</span>
          </div>
        </div>
      );
    case 'survey':
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-[9px] font-bold" style={{ color: AD }}><span className="w-3 h-3 rounded-sm" style={{ background: AD }} /> Sponsored survey</div>
          <Bar w="85%" c="#c9cdd4" />
          {['Very happy', 'Happy', 'Not sure'].map((t, i) => (
            <div key={t} className={`flex items-center gap-1.5 rounded border px-1.5 py-1 text-[8px] ${i === 0 ? 'border-[#2f51d6] text-[#2f51d6]' : 'border-[#e6e8ee] text-[#8f959e]'}`}>
              <span className={`w-2 h-2 rounded-full border ${i === 0 ? 'bg-[#2f51d6] border-[#2f51d6]' : 'border-[#c9cdd4]'}`} />{t}
            </div>
          ))}
        </div>
      );
    case 'reward':
      return (
        <div className="grid grid-cols-3 gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className={`rounded-md p-1.5 space-y-1 ${i === 1 ? 'ring-2' : 'bg-[#f7f8fa]'}`} style={i === 1 ? ({ '--tw-ring-color': AD, background: AD_SOFT } as CSSProperties) : undefined}>
              <div className={`h-8 rounded ${i === 1 ? '' : 'bg-[#eceef2]'} flex items-center justify-center`} style={i === 1 ? { background: AD } : undefined}>
                {i === 1 && <span className="text-[8px] font-extrabold text-white">YOUR LOGO</span>}
              </div>
              <Bar w="80%" c={i === 1 ? AD : LINE} /><Bar w="50%" c={i === 1 ? '#b8c6ff' : LINE} />
            </div>
          ))}
        </div>
      );
    case 'fullscreen':
      return (
        <div className="relative h-full min-h-[96px]">
          <div className="space-y-1.5 opacity-40"><Bar w="70%" /><Bar /><Bar w="85%" /><Bar w="60%" /></div>
          <div className="absolute inset-0 rounded-md flex flex-col items-center justify-center gap-1" style={{ background: AD }}>
            <span className="absolute top-1 right-1.5 text-white/80 text-[9px]">✕</span>
            <span className="text-white font-extrabold text-sm">FULL-SCREEN AD</span>
            <span className="text-[8px] font-bold rounded px-2 py-0.5 bg-white" style={{ color: AD }}>Learn more</span>
          </div>
        </div>
      );
    case 'push':
      return (
        <div className="space-y-2">
          <div className="space-y-1.5 opacity-50"><Bar w="60%" /><Bar /><Bar w="80%" /></div>
          <div className="ml-auto w-3/4 rounded-lg shadow-md border border-[#e6e8ee] bg-white p-1.5 flex gap-1.5 items-center">
            <span className="w-5 h-5 rounded shrink-0" style={{ background: AD }} />
            <div className="flex-1 space-y-1"><Bar w="70%" c="#1f2329" h={5} /><Bar w="90%" h={4} /></div>
          </div>
        </div>
      );
  }
}

/** Phone content for each format. */
function Phone({ id }: { id: AdFormatId }) {
  switch (id) {
    case 'banner':
      return (<div className="space-y-1.5"><AdBlock className="h-7 text-[10px]" /><Bar w="70%" /><Bar /><div className="rounded bg-[#f2f3f5] h-8" /><Bar w="60%" /></div>);
    case 'native':
      return (
        <div className="space-y-1.5">
          <Bar w="60%" /><div className="rounded bg-[#f2f3f5] h-6" />
          <div className="rounded-md p-1 space-y-1" style={{ background: AD_SOFT }}><AdBlock className="h-8 text-[10px]" /><Bar w="80%" c={AD} h={4} /></div>
        </div>
      );
    case 'survey':
      return (
        <div className="space-y-1.5">
          <div className="text-[7px] font-bold" style={{ color: AD }}>Sponsored · +50 pts</div>
          <Bar w="90%" c="#c9cdd4" h={4} />
          {[0, 1, 2].map((i) => (<div key={i} className={`h-3.5 rounded border ${i === 0 ? 'border-[#2f51d6]' : 'border-[#e6e8ee]'}`} />))}
          <div className="h-4 rounded" style={{ background: AD }} />
        </div>
      );
    case 'reward':
      return (
        <div className="space-y-1.5">
          <div className="h-12 rounded-md flex items-center justify-center text-[8px] font-extrabold text-white" style={{ background: AD }}>YOUR LOGO</div>
          <Bar w="70%" c={AD} h={4} /><Bar w="40%" h={4} />
          <div className="h-4 rounded border border-[#2f51d6] text-[7px] flex items-center justify-center font-bold" style={{ color: AD }}>Redeem 800 pts</div>
        </div>
      );
    case 'fullscreen':
      return (
        <div className="h-full min-h-[92px] rounded-md flex flex-col items-center justify-center gap-1 relative" style={{ background: AD }}>
          <span className="absolute top-0.5 right-1 text-white/80 text-[8px]">✕</span>
          <span className="text-white font-extrabold text-[10px]">AD</span>
          <span className="text-[7px] font-bold rounded px-1.5 py-0.5 bg-white" style={{ color: AD }}>Open</span>
        </div>
      );
    case 'push':
      return (
        <div className="space-y-1.5">
          <div className="text-center text-[11px] font-bold text-[#1f2329]">9:41</div>
          <div className="rounded-md bg-white/90 border border-[#e6e8ee] shadow p-1 flex gap-1 items-center">
            <span className="w-3.5 h-3.5 rounded shrink-0" style={{ background: AD }} />
            <div className="flex-1 space-y-0.5"><Bar w="80%" c="#1f2329" h={3} /><Bar h={3} /></div>
          </div>
        </div>
      );
  }
}

function Frame({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`bg-white border-2 rounded-lg overflow-hidden ${className}`} style={{ borderColor: AD }}>{children}</div>;
}

/** Browser window with a phone overlapping its right edge, drawing one ad format. */
export default function AdMockup({ id }: { id: AdFormatId }) {
  return (
    <div className="relative w-full max-w-[300px] h-[190px] mx-auto" aria-hidden="true">
      <Frame className="absolute left-0 top-2 w-[78%] h-[160px]">
        <div className="h-4 flex items-center gap-1 px-1.5" style={{ background: AD }}>
          {[0, 1, 2].map((i) => <span key={i} className="w-1.5 h-1.5 rounded-full bg-white/70" />)}
        </div>
        <div className="p-2.5"><Desktop id={id} /></div>
      </Frame>
      <Frame className={`absolute right-0 bottom-0 w-[34%] h-[160px] rounded-[14px] ${id === 'push' ? 'bg-gradient-to-b from-[#dfe6ff] to-white' : ''}`}>
        <div className="mx-auto mt-1 mb-1.5 w-8 h-1.5 rounded-full" style={{ background: AD }} />
        <div className="px-1.5 h-[calc(100%-18px)]"><Phone id={id} /></div>
      </Frame>
    </div>
  );
}
